import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ShieldCheck, AlertTriangle, Clock, CheckCircle, FileText } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Mock verification data
const verificationAlerts = [
  { id: 1, name: "Ahmad Faizal", type: "License Expiry", days: 15, severity: "high" },
  { id: 2, name: "Dr. Kavitha", type: "License Expiry", days: 30, severity: "medium" },
  { id: 3, name: "Lee Wei Ming", type: "Certificate Renewal", days: 45, severity: "low" },
  { id: 4, name: "Siti Aminah", type: "License Expiry", days: 60, severity: "low" },
];

const auditLog = [
  { id: 1, action: "License Verified", therapist: "Dr. Nurul Aisyah", admin: "Admin", timestamp: "2025-01-01 10:30" },
  { id: 2, action: "Certificate Approved", therapist: "Ahmad Faizal", admin: "Admin", timestamp: "2025-01-01 09:15" },
  { id: 3, action: "Document Requested", therapist: "Lee Wei Ming", admin: "Admin", timestamp: "2024-12-31 16:45" },
  { id: 4, action: "Account Suspended", therapist: "Test User", admin: "Admin", timestamp: "2024-12-31 14:20" },
];

export default function AdminVerification() {
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/admin/therapists`, { withCredentials: true });
        setTherapists(response.data);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const verifiedCount = therapists.filter(t => t.status === "verified" || t.status === "active").length;
  const pendingCount = therapists.filter(t => t.status === "pending").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-verification">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-50 rounded-lg flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Fully Verified</p>
                <p className="text-2xl font-heading font-bold text-slate-900">{verifiedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-50 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Pending Verification</p>
                <p className="text-2xl font-heading font-bold text-slate-900">{pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Expiring Soon</p>
                <p className="text-2xl font-heading font-bold text-slate-900">{verificationAlerts.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Compliance Rate</p>
                <p className="text-2xl font-heading font-bold text-slate-900">94%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expiry Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              License & Certificate Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {verificationAlerts.map((alert) => (
                <div 
                  key={alert.id} 
                  className={`p-4 rounded-lg border ${
                    alert.severity === "high" ? "bg-red-50 border-red-200" :
                    alert.severity === "medium" ? "bg-amber-50 border-amber-200" :
                    "bg-slate-50 border-slate-200"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-slate-900">{alert.name}</p>
                      <p className="text-sm text-slate-600">{alert.type}</p>
                    </div>
                    <Badge className={
                      alert.severity === "high" ? "bg-red-100 text-red-700" :
                      alert.severity === "medium" ? "bg-amber-100 text-amber-700" :
                      "bg-slate-100 text-slate-700"
                    }>
                      {alert.days} days
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Audit Log */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Verification Audit Log</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {auditLog.map((log) => (
                <div key={log.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">{log.action}</p>
                    <p className="text-xs text-slate-500">{log.therapist} • by {log.admin}</p>
                    <p className="text-xs text-slate-400 mt-1">{log.timestamp}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Verification Status Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Therapist Verification Status</CardTitle>
        </CardHeader>
        <CardContent>
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Therapist</TableHead>
                <TableHead>NRIC</TableHead>
                <TableHead>License</TableHead>
                <TableHead>Certificates</TableHead>
                <TableHead>Overall Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {therapists.slice(0, 8).map((th) => (
                <TableRow key={th.therapist_id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={th.picture} />
                        <AvatarFallback className="bg-primary text-white text-sm">
                          {th.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="font-medium">{th.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={th.status !== "pending" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                      {th.status !== "pending" ? "Verified" : "Pending"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={th.status !== "pending" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                      {th.status !== "pending" ? "Valid" : "Pending"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm">{th.certifications?.length || 0} certs</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Progress 
                        value={th.status === "active" ? 100 : th.status === "verified" ? 75 : 30} 
                        className="w-20 h-2" 
                      />
                      <span className="text-xs text-slate-500">
                        {th.status === "active" ? "100%" : th.status === "verified" ? "75%" : "30%"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button variant="outline" size="sm" data-testid={`verify-${th.therapist_id}`}>
                      Review
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
